<template>
  <div class="payroll-page">
    <!-- ================= HERO ================= -->
    <header class="pr-hero">
      <div class="pr-hero-grid">
        <div class="pr-hero-main">
          <div class="pr-hero-title">
            PROCESS PAYROLL
            <span class="pr-count-pill">{{ reportRows.length }}</span>
          </div>
          <div class="pr-hero-sub">
            Select a salesperson, then pick invoices to pay, suspend, or override commission.
          </div>
        </div>
      </div>
    </header>

    <!-- ================= FILTERS ================= -->
    <div class="pr-filterstrip">
      <div class="d-flex flex-wrap align-items-end" style="gap: 15px;">
        <div style="width: 260px;">
          <label class="pr-label">Salesperson <span class="pr-required">*</span></label>
          <b-form-select
            v-model="selectedSalesperson"
            :options="salespersonOptions"
            size="sm"
            @input="onSalespersonChange"
          >
            <template #first>
              <b-form-select-option :value="null">-- Select a Salesperson --</b-form-select-option>
            </template>
          </b-form-select>
        </div>

        <div style="width: 240px;">
          <label class="pr-label">Sales Location</label>
          <b-form-select
            v-model="selectedLocation"
            :options="locationOptions"
            size="sm"
            @input="onFilterChange"
          >
            <template #first>
              <b-form-select-option :value="null">All Locations</b-form-select-option>
            </template>
          </b-form-select>
        </div>

        <div style="width: 260px;">
          <label class="pr-label">Date Range</label>
          <date-picker
            v-model="dateRange"
            range
            format="MM-DD-YYYY"
            valueType="format"
            :clearable="true"
            :editable="false"
            placeholder="This week"
            style="width: 100%;"
          ></date-picker>
        </div>

        <div style="display: flex; gap: 6px; margin-bottom: 2px;">
          <b-button size="sm" variant="outline-secondary" @click="setDate('this_week')">This Week</b-button>
          <b-button size="sm" variant="outline-secondary" @click="setDate('last_week')">Last Week</b-button>
          <b-button size="sm" variant="outline-secondary" @click="setDate('this_month')">This Month</b-button>
          <b-button size="sm" variant="outline-secondary" @click="setDate('last_month')">Last Month</b-button>
        </div>

        <div class="ml-auto">
          <b-button
            class="pr-btn-primary"
            size="sm"
            @click="fetchReport"
            :disabled="!selectedSalesperson || loading"
          >
            <b-spinner small v-if="loading"></b-spinner>
            <b-icon v-else icon="arrow-clockwise"></b-icon>
            {{ loading ? 'Loading…' : 'Analyze' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- ================= KPIs ================= -->
    <div v-if="selectedSalesperson && !loading" class="pr-kpis">
      <div class="pr-kpi pr-kpi-unpaid">
        <div class="pr-kpi-label">UNPAID COMMISSION</div>
        <div class="pr-kpi-value">${{ fmt(totalUnpaidCommission) }}</div>
        <div class="pr-kpi-sub">{{ payableRows.length }} invoice(s)</div>
      </div>
      <div class="pr-kpi pr-kpi-selected">
        <div class="pr-kpi-label">SELECTED — TO PAY</div>
        <div class="pr-kpi-value">${{ fmt(selectedPayableCommission) }}</div>
        <div class="pr-kpi-sub">{{ selectedInvoiceIds.length }} selected</div>
      </div>
      <div class="pr-kpi pr-kpi-suspended">
        <div class="pr-kpi-label">SUSPENDED</div>
        <div class="pr-kpi-value">${{ fmt(totalSuspendedCommission) }}</div>
        <div class="pr-kpi-sub">{{ suspendedRows.length }} invoice(s)</div>
      </div>
      <div class="pr-kpi pr-kpi-paid">
        <div class="pr-kpi-label">PAID (ALL-TIME)</div>
        <div class="pr-kpi-value">${{ fmt(totalPaidAllTime) }}</div>
        <div class="pr-kpi-sub">{{ paidReceipts.length }} receipt(s)</div>
      </div>
    </div>

    <!-- ================= EMPTY / LOADING ================= -->
    <div v-if="!selectedSalesperson" class="pr-empty">
      <b-icon icon="person-badge" font-scale="2.5"></b-icon>
      <h3>Select a salesperson to begin</h3>
      <p>Choose a salesperson above to load their payroll data for the selected range.</p>
    </div>

    <div v-else-if="loading" class="pr-loading">
      <b-spinner></b-spinner>
      <span>Loading payroll data…</span>
    </div>

    <!-- ================= SUB-TABS ================= -->
    <template v-else>
      <b-tabs v-model="innerTab" content-class="pr-inner-content" class="pr-tabs-inner">

        <!-- ======================= DETAILED ======================= -->
        <b-tab :title="`Detailed (${payableRows.length})`">
          <div class="pr-toolbar">
            <div class="pr-toolbar-info">
              <span v-if="selectedInvoiceIds.length === 0" class="text-muted">
                Select invoices below to take action.
              </span>
              <span v-else>
                <strong>{{ selectedInvoiceIds.length }}</strong> selected —
                Payable: <strong class="text-success">${{ fmt(selectedPayableCommission) }}</strong>
                <span v-if="selectedSalespeopleCount > 1" class="pr-warn-chip">
                  <b-icon icon="info-circle"></b-icon>
                  {{ selectedSalespeopleCount }} salespeople — one receipt each
                </span>
              </span>
            </div>
            <div class="pr-toolbar-actions">
              <b-button
                class="pr-btn-primary"
                size="sm"
                :disabled="selectedInvoiceIds.length === 0 || isProcessing"
                @click="openPayModal"
              >
                <b-icon icon="cash-stack"></b-icon>
                Mark Paid ({{ selectedInvoiceIds.length }}) — ${{ fmt(selectedPayableCommission) }}
              </b-button>
              <b-button
                variant="outline-warning"
                size="sm"
                :disabled="selectedInvoiceIds.length === 0 || isProcessing"
                @click="openSuspendModal"
              >
                <b-icon icon="pause-circle"></b-icon>
                Suspend ({{ selectedInvoiceIds.length }})
              </b-button>
              <b-button
                variant="outline-info"
                size="sm"
                :disabled="selectedDiscountOverridable.length === 0 || isProcessing"
                @click="allowDiscountCommission"
              >
                <b-icon icon="shield-check"></b-icon>
                Allow Discount Comm ({{ selectedDiscountOverridable.length }})
              </b-button>
            </div>
          </div>

          <div class="pr-table-wrap">
            <table class="pr-table">
              <thead>
                <tr>
                  <th style="width:36px;">
                    <b-form-checkbox
                      :checked="allPayableSelected"
                      :indeterminate="somePayableSelected"
                      :disabled="payableRows.length === 0"
                      @change="toggleAllPayable"
                    />
                  </th>
                  <th>Date</th>
                  <th>Invoice #</th>
                  <th>Location</th>
                  <th>Salesperson</th>
                  <th class="text-right">Order Total</th>
                  <th class="text-right">Discount</th>
                  <th class="text-center">Disc %</th>
                  <th class="text-right">Base Comm</th>
                  <th class="text-right">Other Comm</th>
                  <th class="text-right">Payable Comm</th>
                  <th class="text-center">Flags</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="payableRows.length === 0">
                  <td colspan="12" class="text-center p-5 text-muted">
                    No unpaid invoices for this salesperson in the selected range.
                  </td>
                </tr>
                <tr
                  v-for="row in payableRows"
                  :key="row.invoice_id"
                  class="pr-row"
                  :class="{ 'is-selected': isSelected(row.invoice_id) }"
                >
                  <td>
                    <b-form-checkbox
                      :checked="isSelected(row.invoice_id)"
                      @change="toggleRow(row.invoice_id, $event)"
                    />
                  </td>
                  <td class="pr-td">{{ formatDate(row.ts) }}</td>
                  <td class="pr-td">
                    <a href="#" class="pr-link" @click.prevent="openInvoice(row.invoice_id)">
                      {{ row.invoice_id }}
                    </a>
                  </td>
                  <td class="pr-td">{{ getLocationName(row.location_id) }}</td>
                  <td class="pr-td">{{ row.username }}</td>
                  <td class="pr-td text-right">${{ fmt(row.order_total) }}</td>
                  <td class="pr-td text-right">${{ fmt(row.total_discount) }}</td>
                  <td class="pr-td text-center" :class="{ 'pr-disc-flag': (row.discount_percentage || 0) >= 5 }">
                    {{ (row.discount_percentage || 0).toFixed(2) }}%
                  </td>
                  <td class="pr-td text-right">${{ fmt(row.adjusted_base_commission) }}</td>
                  <td class="pr-td text-right">${{ fmt(otherCommission(row)) }}</td>
                  <td class="pr-td text-right">
                    <strong class="pr-payable">${{ fmt(payableCommission(row)) }}</strong>
                  </td>
                  <td class="pr-td text-center">
                    <b-badge v-if="row.commission_override" variant="info" class="pr-mini">OVERRIDE</b-badge>
                    <b-badge v-if="(row.discount_percentage || 0) >= 5 && !row.commission_override" variant="warning" class="pr-mini">DISC-RULE</b-badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-tab>

        <!-- ======================= SUSPENDED ======================= -->
        <b-tab :title="`Suspended (${suspendedRows.length})`">
          <div class="pr-toolbar">
            <div class="pr-toolbar-info">
              <span v-if="suspendedSelectedIds.length === 0" class="text-muted">
                Suspended commissions sit here. Restore them to make them payable again.
              </span>
              <span v-else>
                <strong>{{ suspendedSelectedIds.length }}</strong> selected —
                Restore: <strong>${{ fmt(suspendedSelectedCommission) }}</strong>
              </span>
            </div>
            <div class="pr-toolbar-actions">
              <b-button
                variant="outline-success"
                size="sm"
                :disabled="suspendedSelectedIds.length === 0 || isProcessing"
                @click="restoreCommission"
              >
                <b-spinner small v-if="isProcessing"></b-spinner>
                <b-icon v-else icon="arrow-counterclockwise"></b-icon>
                Restore ({{ suspendedSelectedIds.length }})
              </b-button>
            </div>
          </div>

          <div class="pr-table-wrap">
            <table class="pr-table">
              <thead>
                <tr>
                  <th style="width:36px;">
                    <b-form-checkbox
                      :checked="allSuspendedSelected"
                      :disabled="suspendedRows.length === 0"
                      @change="toggleAllSuspended"
                    />
                  </th>
                  <th>Date</th>
                  <th>Invoice #</th>
                  <th>Location</th>
                  <th>Salesperson</th>
                  <th class="text-right">Order Total</th>
                  <th class="text-right">Suspended Comm</th>
                  <th>Reason</th>
                  <th>Suspended By</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="suspendedRows.length === 0">
                  <td colspan="9" class="text-center p-5 text-muted">
                    No suspended commissions for this salesperson.
                  </td>
                </tr>
                <tr v-for="row in suspendedRows" :key="row.invoice_id" class="pr-row">
                  <td>
                    <b-form-checkbox
                      :checked="suspendedSelectedIds.includes(String(row.invoice_id))"
                      @change="toggleSuspendedRow(row.invoice_id, $event)"
                    />
                  </td>
                  <td class="pr-td">{{ formatDate(row.ts) }}</td>
                  <td class="pr-td">
                    <a href="#" class="pr-link" @click.prevent="openInvoice(row.invoice_id)">
                      {{ row.invoice_id }}
                    </a>
                  </td>
                  <td class="pr-td">{{ getLocationName(row.location_id) }}</td>
                  <td class="pr-td">{{ row.username }}</td>
                  <td class="pr-td text-right">${{ fmt(row.order_total) }}</td>
                  <td class="pr-td text-right">
                    <strong class="pr-payable">${{ fmt(row.adjusted_total_commission) }}</strong>
                  </td>
                  <td class="pr-td">{{ row.suspend_reason || '—' }}</td>
                  <td class="pr-td">{{ row.suspended_by || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-tab>

        <!-- ======================= PAID PAYROLL ======================= -->
        <b-tab :title="`Paid Payroll (${paidReceipts.length})`">
          <div v-if="paidLoading" class="p-5 text-center text-muted">
            <b-spinner small></b-spinner> Loading payroll receipts…
          </div>
          <template v-else>
            <div class="pr-stats-grid">
              <div
                v-for="stat in salespersonPaidStats"
                :key="stat.salesperson"
                class="pr-stat-card"
                :class="{ 'is-active': stat.salesperson === selectedSalesperson }"
              >
                <div class="pr-stat-name">{{ stat.salesperson }}</div>
                <div class="pr-stat-row"><span>This Week</span><b>${{ fmt(stat.this_week) }}</b></div>
                <div class="pr-stat-row"><span>This Month</span><b>${{ fmt(stat.this_month) }}</b></div>
                <div class="pr-stat-row"><span>All Time</span><b class="text-success">${{ fmt(stat.total) }}</b></div>
                <div class="pr-stat-row"><span>Receipts</span><b>{{ stat.count }}</b></div>
              </div>
              <div v-if="salespersonPaidStats.length === 0" class="pr-empty-mini">
                No payroll receipts yet. Mark invoices paid from the Detailed tab to see receipts here.
              </div>
            </div>

            <div class="pr-table-wrap">
              <table class="pr-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Receipt #</th>
                    <th>Salesperson</th>
                    <th>Invoices Paid</th>
                    <th class="text-right">Total</th>
                    <th>Processed By</th>
                    <th class="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="filteredPaidReceipts.length === 0">
                    <td colspan="7" class="text-center p-5 text-muted">
                      No payroll receipts found.
                    </td>
                  </tr>
                  <tr v-for="rec in filteredPaidReceipts" :key="rec.id" class="pr-row">
                    <td class="pr-td">{{ rec.payment_date }}</td>
                    <td class="pr-td"><strong>{{ rec.payment_receipt }}</strong></td>
                    <td class="pr-td">{{ rec.salesperson }}</td>
                    <td class="pr-td">
                      <span
                        v-for="(inv, idx) in rec.invoices_json"
                        :key="idx"
                        class="pr-chip"
                      >{{ inv.invoice_id || inv.ref }}</span>
                    </td>
                    <td class="pr-td text-right text-success font-weight-bold">${{ fmt(rec.total_paid_amount) }}</td>
                    <td class="pr-td">{{ rec.processed_by }}</td>
                    <td class="pr-td text-center">
                      <b-button class="pr-btn-primary" size="sm" @click="openPayrollReceipt(rec)">
                        <b-icon icon="eye"></b-icon> Preview
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </b-tab>
      </b-tabs>
    </template>

    <!-- ================= MARK PAID MODAL ================= -->
    <b-modal
      id="payroll-pay-modal"
      hide-footer hide-header centered scrollable no-close-on-backdrop
      dialog-class="pr-modal-dialog modal-xl" content-class="pr-modal-content"
    >
      <div class="pr-doc">
        <button class="pr-close" @click="$bvModal.hide('payroll-pay-modal')">×</button>

        <div class="pr-doc-header">
          <div>
            <div class="pr-doc-label">PAYROLL RECEIPT — SUBMIT</div>
            <div class="pr-doc-title">MECCA WHOLESALE LLC</div>
            <div class="pr-doc-sub">101 E Venango Street, Philadelphia, PA 19134</div>
          </div>
          <div class="pr-doc-meta">
            <div><span>Date</span><b>{{ todayDate }}</b></div>
            <div><span>Receipt #</span><b class="text-muted">auto-generated</b></div>
          </div>
        </div>

        <b-alert v-if="payError" show variant="danger" class="mt-3 mb-0">{{ payError }}</b-alert>

        <div v-for="group in payGroups" :key="group.salesperson" class="pr-group">
          <div class="pr-group-header">
            <div>
              <span class="pr-group-name">{{ group.salesperson }}</span>
              <span class="pr-group-count">— {{ group.rows.length }} invoice(s)</span>
            </div>
            <b class="pr-group-total">${{ fmt(group.total) }}</b>
          </div>
          <table class="pr-doc-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Location</th>
                <th class="text-right">Payable Comm</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in group.rows" :key="row.invoice_id">
                <td>{{ row.invoice_id }}</td>
                <td>{{ formatDate(row.ts) }}</td>
                <td>{{ getLocationName(row.location_id) }}</td>
                <td class="text-right">${{ fmt(payableCommission(row)) }}</td>
              </tr>
            </tbody>
          </table>
          <div class="pr-group-fields">
            <div style="flex: 1;">
              <label class="pr-label">Reference # <span class="pr-required">*</span></label>
              <b-form-input
                v-model="group.reference"
                placeholder="Check #, ACH ref, etc."
                size="sm"
                :state="group.reference && group.reference.trim() !== '' ? true : null"
              />
            </div>
            <div style="flex: 1;">
              <label class="pr-label">Comments</label>
              <b-form-input v-model="group.comments" placeholder="Optional" size="sm" />
            </div>
          </div>
        </div>

        <div class="pr-grand">
          <span>TOTAL TO PAY:</span>
          <span>${{ fmt(payTotal) }}</span>
        </div>

        <div class="pr-modal-actions">
          <b-button variant="outline-secondary" @click="$bvModal.hide('payroll-pay-modal')">CANCEL</b-button>
          <b-button variant="dark" :disabled="isProcessing || !canSubmitPay" @click="submitPayroll">
            <b-spinner small v-if="isProcessing"></b-spinner>
            <b-icon v-else icon="check2-circle"></b-icon>
            {{ isProcessing ? 'PROCESSING…' : 'SUBMIT PAYROLL' }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- ================= SUSPEND MODAL ================= -->
    <b-modal
      id="payroll-suspend-modal"
      hide-footer hide-header centered no-close-on-backdrop
      dialog-class="pr-suspend-dialog" content-class="pr-modal-content"
    >
      <div class="pr-doc" style="padding: 24px;">
        <button class="pr-close" @click="$bvModal.hide('payroll-suspend-modal')">×</button>
        <h3 class="pr-doc-title" style="margin-bottom: 8px;">Suspend Commission</h3>
        <p class="text-muted mb-3">
          Suspending <strong>{{ selectedInvoiceIds.length }}</strong> invoice(s)
          totaling <strong>${{ fmt(selectedPayableCommission) }}</strong>.
          They will move to the Suspended tab and can be restored anytime.
        </p>
        <label class="pr-label">Reason <span class="pr-required">*</span></label>
        <b-form-textarea
          v-model="suspendReason"
          placeholder="e.g. pending review, dispute, etc."
          rows="3"
          size="sm"
          :state="suspendReason && suspendReason.trim() ? true : null"
        />
        <div class="pr-modal-actions" style="margin-top: 16px;">
          <b-button variant="outline-secondary" @click="$bvModal.hide('payroll-suspend-modal')">CANCEL</b-button>
          <b-button
            variant="warning"
            :disabled="!suspendReason || !suspendReason.trim() || isProcessing"
            @click="submitSuspend"
          >
            <b-spinner small v-if="isProcessing"></b-spinner>
            <b-icon v-else icon="pause-circle"></b-icon>
            {{ isProcessing ? 'SUSPENDING…' : 'CONFIRM SUSPEND' }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- ================= RECEIPT PREVIEW ================= -->
    <b-modal
      id="payroll-receipt-modal"
      hide-footer hide-header centered scrollable
      dialog-class="pr-modal-dialog modal-xl" content-class="pr-modal-content"
    >
      <div v-if="currentReceipt" class="pr-doc" style="padding: 24px;">
        <button class="pr-close" @click="$bvModal.hide('payroll-receipt-modal')">×</button>
        <div class="pr-doc-header">
          <div>
            <div class="pr-doc-label">PAYROLL RECEIPT</div>
            <div class="pr-doc-title">MECCA WHOLESALE LLC</div>
            <div class="pr-doc-sub">101 E Venango Street, Philadelphia, PA 19134</div>
          </div>
          <div class="pr-doc-meta">
            <div><span>Date</span><b>{{ currentReceipt.payment_date }}</b></div>
            <div><span>Receipt #</span><b class="pr-underlined">{{ currentReceipt.payment_receipt }}</b></div>
            <div><span>Reference #</span><b>{{ currentReceipt.reference_number }}</b></div>
          </div>
        </div>

        <div class="pr-group-header" style="margin-top: 20px;">
          <span class="pr-group-name">Paid to: {{ currentReceipt.salesperson }}</span>
          <b class="pr-group-total">${{ fmt(currentReceipt.total_paid_amount) }}</b>
        </div>

        <table class="pr-doc-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Date</th>
              <th>Location</th>
              <th class="text-right">Payable Comm</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(inv, idx) in currentReceipt.invoices_json" :key="idx">
              <td>{{ inv.invoice_id || inv.ref }}</td>
              <td>{{ inv.ts ? formatDate(inv.ts) : '—' }}</td>
              <td>{{ inv.location_id ? getLocationName(inv.location_id) : '—' }}</td>
              <td class="text-right">${{ fmt(inv.payable_commission) }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="currentReceipt.comments && currentReceipt.comments.trim()" class="mt-3">
          <label class="pr-label">Comments</label>
          <div class="pr-comments-box">{{ currentReceipt.comments }}</div>
        </div>

        <div class="pr-grand">
          <span>TOTAL PAID:</span>
          <span>${{ fmt(currentReceipt.total_paid_amount) }}</span>
        </div>

        <div style="text-align: center; margin-top: 16px;">
          <b-button variant="dark" @click="printReceipt">
            <b-icon icon="printer"></b-icon> Print Receipt
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';

const PAYROLL_ENDPOINTS = {
  detailed:          '/api/reports/reports_v5/',
  processPayroll:    '/api/sales/process_payroll/',
  paidReceipts:      '/api/sales/get_payroll_receipts/',
  suspendCommission: '/api/sales/suspend_commission/',
  restoreCommission: '/api/sales/restore_commission/',
  allowDiscountComm: '/api/sales/allow_discount_commission/',
};

export default {
  name: 'PayrollPage',
  components: { DatePicker },
  data() {
    return {
      packet: {},
      loading: false,
      paidLoading: false,
      isProcessing: false,

      selectedSalesperson: null,
      selectedLocation: null,
      dateRange: null,

      innerTab: 0,
      reportRows: [],
      paidReceipts: [],

      // Populated once from an unfiltered report fetch
      knownSalespeople: [],

      selectedInvoiceIds: [],
      suspendedSelectedIds: [],

      payGroups: [],
      payError: null,

      suspendReason: '',
      currentReceipt: null,
    };
  },
  computed: {
    todayDate() { return moment().format('MM/DD/YYYY'); },

    locationOptions() {
      const locs = this.packet.locations || [];
      return locs
        .filter(l => !['0','40','41','42','47','46'].includes(String(l.value)))
        .map(l => ({ value: l.value, text: l.text }));
    },

    salespersonOptions() {
      // 1. Prefer explicitly loaded salespeople from unfiltered report
      if (this.knownSalespeople.length > 0) {
        return this.knownSalespeople.map(n => ({ value: n, text: n }));
      }
      // 2. Fallback: packet.user details / users if present
      const users = this.packet.users || this.packet.salespersons || [];
      if (users.length > 0) {
        return users
          .filter(u => u.username || u.value)
          .map(u => ({
            value: u.username || u.value,
            text:  u.fullname || u.name || u.text || u.username || u.value,
          }))
          .sort((a, b) => a.text.localeCompare(b.text));
      }
      // 3. Last-resort fallback: whatever we've already seen
      const set = new Set(this.reportRows.map(r => r.username).filter(Boolean));
      return Array.from(set).sort().map(n => ({ value: n, text: n }));
    },

    /* ---------- row buckets ---------- */
    payableRows() {
      return this.reportRows.filter(r => !r.commission_suspended && !r.payroll_receipt_id);
    },
    suspendedRows() {
      return this.reportRows.filter(r => r.commission_suspended);
    },
    overrideRows() {
      return this.reportRows.filter(r => r.commission_override);
    },

    /* ---------- totals ---------- */
    totalUnpaidCommission() {
      return this.payableRows.reduce((s, r) => s + this.payableCommission(r), 0);
    },
    totalSuspendedCommission() {
      return this.suspendedRows.reduce((s, r) => s + Number(r.adjusted_total_commission || 0), 0);
    },
    totalPaidAllTime() {
      return this.paidReceipts.reduce((s, r) => s + Number(r.total_paid_amount || 0), 0);
    },
    selectedPayableCommission() {
      return this.payableRows
        .filter(r => this.selectedInvoiceIds.includes(String(r.invoice_id)))
        .reduce((s, r) => s + this.payableCommission(r), 0);
    },
    selectedSalespeopleCount() {
      const set = new Set(
        this.payableRows
          .filter(r => this.selectedInvoiceIds.includes(String(r.invoice_id)))
          .map(r => r.username)
      );
      return set.size;
    },
    suspendedSelectedCommission() {
      return this.suspendedRows
        .filter(r => this.suspendedSelectedIds.includes(String(r.invoice_id)))
        .reduce((s, r) => s + Number(r.adjusted_total_commission || 0), 0);
    },

    /* ---------- selection helpers ---------- */
    allPayableSelected() {
      return this.payableRows.length > 0 &&
        this.payableRows.every(r => this.selectedInvoiceIds.includes(String(r.invoice_id)));
    },
    somePayableSelected() {
      const n = this.payableRows.filter(r => this.selectedInvoiceIds.includes(String(r.invoice_id))).length;
      return n > 0 && n < this.payableRows.length;
    },
    allSuspendedSelected() {
      return this.suspendedRows.length > 0 &&
        this.suspendedRows.every(r => this.suspendedSelectedIds.includes(String(r.invoice_id)));
    },
    selectedDiscountOverridable() {
      return this.payableRows.filter(r =>
        this.selectedInvoiceIds.includes(String(r.invoice_id)) &&
        Number(r.discount_percentage || 0) >= 5 &&
        !r.commission_override
      );
    },

    /* ---------- pay modal ---------- */
    payTotal() {
      return this.payGroups.reduce((s, g) => s + g.total, 0);
    },
    canSubmitPay() {
      if (this.payGroups.length === 0) return false;
      return this.payGroups.every(g => g.reference && g.reference.trim() !== '');
    },

    /* ---------- paid stats ---------- */
    salespersonPaidStats() {
      const map = {};
      const now = moment();
      const weekStart  = now.clone().startOf('isoWeek');
      const monthStart = now.clone().startOf('month');

      for (const rec of this.paidReceipts) {
        const sp = rec.salesperson || 'Unknown';
        if (!map[sp]) map[sp] = { salesperson: sp, this_week: 0, this_month: 0, total: 0, count: 0 };
        const amt = Number(rec.total_paid_amount || 0);
        map[sp].total += amt;
        map[sp].count += 1;
        const d = moment(rec.payment_date, [
          'MM-DD-YYYY HH:mm:ss A',
          'MM-DD-YYYY HH:mm A',
          'MM-DD-YYYY',
          'YYYY-MM-DD HH:mm:ss',
          'YYYY-MM-DD',
        ], true);
        if (d.isValid()) {
          if (d.isSameOrAfter(weekStart))  map[sp].this_week  += amt;
          if (d.isSameOrAfter(monthStart)) map[sp].this_month += amt;
        }
      }
      return Object.values(map);
    },

    filteredPaidReceipts() {
      if (!this.selectedSalesperson) return this.paidReceipts;
      return this.paidReceipts.filter(r => r.salesperson === this.selectedSalesperson);
    },
  },
  watch: {
    selectedSalesperson() {
      this.selectedInvoiceIds = [];
      this.suspendedSelectedIds = [];
    },
  },
  async created() {
    this.packet = JSON.parse(localStorage.packet || '{}');
    this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');

    // ✅ IMPORTANT: set date BEFORE first fetch
    this.setDate('this_week');

    // ✅ Load salespeople from an unfiltered report fetch
    await this.loadSalespeople();

    // ✅ Paid receipts can load in parallel
    this.fetchPaidReceipts();
  },
  methods: {
    fmt(v) { return (parseFloat(v) || 0).toFixed(2); },
    formatDate(ts) { return ts ? moment(ts).format('MM-DD-YYYY HH:mm A') : '—'; },
    getLocationName(id) {
      if (id === null || id === undefined || id === '') return '—';
      const idStr = String(id).trim();
      if (['0','40','41','42'].includes(idStr)) return 'MECCA WHOLESALE';
      const locs = this.packet.locations || [];
      const found = locs.find(l => String(l.value) === idStr);
      return found ? found.text : `Location #${id}`;
    },
    openInvoice(id) {
      if (!id) return;
      const route = this.$router.resolve({ path: `/sales/viewsale/${id}/` });
      window.open(route.href, '_blank', 'noopener,noreferrer');
    },

    /* ---------- payable commission logic ---------- */
    otherCommission(row) {
      return (
        Number(row.adjusted_delivery_commission || 0) +
        Number(row.adjusted_setup_commission    || 0) +
        Number(row.adjusted_warranty_commission || 0) +
        Number(row.spiff_commission             || 0)
      );
    },
    payableCommission(row) {
      if (row.commission_suspended) return 0;
      const discPct = Number(row.discount_percentage || 0);
      const base    = Number(row.adjusted_base_commission || 0);
      if (discPct >= 5 && !row.commission_override) {
        return this.otherCommission(row);
      }
      return Number(row.adjusted_total_commission || 0);
    },

    /* ---------- filters ---------- */
    setDate(type) {
      const now = moment();
      if (type === 'this_week') {
        this.dateRange = [
          now.clone().startOf('isoWeek').format('MM-DD-YYYY'),
          now.clone().endOf('isoWeek').format('MM-DD-YYYY'),
        ];
      } else if (type === 'last_week') {
        this.dateRange = [
          now.clone().subtract(1, 'week').startOf('isoWeek').format('MM-DD-YYYY'),
          now.clone().subtract(1, 'week').endOf('isoWeek').format('MM-DD-YYYY'),
        ];
      } else if (type === 'this_month') {
        this.dateRange = [
          now.clone().startOf('month').format('MM-DD-YYYY'),
          now.clone().endOf('month').format('MM-DD-YYYY'),
        ];
      } else if (type === 'last_month') {
        this.dateRange = [
          now.clone().subtract(1, 'month').startOf('month').format('MM-DD-YYYY'),
          now.clone().subtract(1, 'month').endOf('month').format('MM-DD-YYYY'),
        ];
      }
      if (this.selectedSalesperson) this.fetchReport();
    },
    onSalespersonChange() {
      if (this.selectedSalesperson) this.fetchReport();
    },
    onFilterChange() {
      if (this.selectedSalesperson) this.fetchReport();
    },

    /* ---------- fetch salesperson list ---------- */
    async loadSalespeople() {
      if (!this.dateRange || this.dateRange.length !== 2) return;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.postAsyncP(PAYROLL_ENDPOINTS.detailed, {
            username: this.packet.userDetails.username,
            dates: this.dateRange,
            location_id: null,
            salesperson: null,                     // no filter — get everyone
            current_user_id: this.packet.userDetails.id,
            userid: this.packet.userDetails.id,
          }, this, resolve, reject);
        });
        const rows = (res && res.data && res.data.result) || [];
        const set = new Set(rows.map(r => r.username).filter(Boolean));
        this.knownSalespeople = Array.from(set).sort();
      } catch (e) {
        console.error('[PAYROLL] loadSalespeople failed:', e);
        this.knownSalespeople = [];
      }
    },

    /* ---------- fetch report ---------- */
    async fetchReport() {
      if (!this.selectedSalesperson) return;
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$bvToast.toast('Please select a date range.', { variant: 'warning', autoHideDelay: 3000 });
        return;
      }
      this.loading = true;
      this.selectedInvoiceIds = [];
      this.suspendedSelectedIds = [];
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.postAsyncP(PAYROLL_ENDPOINTS.detailed, {
            username: this.packet.userDetails.username,
            dates: this.dateRange,
            location_id: this.selectedLocation,
            salesperson: this.selectedSalesperson,
            current_user_id: this.packet.userDetails.id,
            userid: this.packet.userDetails.id,
          }, this, resolve, reject);
        });
        if (res && res.data && res.data.result) {
          this.reportRows = (res.data.result || []).map(r => ({
            ...r,
            commission_suspended: !!r.commission_suspended,
            commission_override:  !!r.commission_override,
          }));
        } else {
          this.reportRows = [];
        }
      } catch (e) {
        console.error('[PAYROLL] fetchReport failed:', e);
        this.reportRows = [];
        this.$bvToast.toast('Failed to load payroll data.', { variant: 'danger', autoHideDelay: 4000 });
      }
      this.loading = false;
    },

    async fetchPaidReceipts() {
      this.paidLoading = true;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getAsyncP(PAYROLL_ENDPOINTS.paidReceipts, {}, this, resolve, reject);
        });
        if (res && res.data && res.data.result) {
          this.paidReceipts = res.data.result;
        }
      } catch (e) {
        console.error('[PAYROLL] fetchPaidReceipts failed:', e);
      }
      this.paidLoading = false;
    },

    /* ---------- selection ---------- */
    isSelected(id) { return this.selectedInvoiceIds.includes(String(id)); },
    toggleRow(id, checked) {
      const set = new Set(this.selectedInvoiceIds);
      if (checked) set.add(String(id)); else set.delete(String(id));
      this.selectedInvoiceIds = Array.from(set);
    },
    toggleAllPayable(checked) {
      this.selectedInvoiceIds = checked
        ? this.payableRows.map(r => String(r.invoice_id))
        : [];
    },
    toggleSuspendedRow(id, checked) {
      const set = new Set(this.suspendedSelectedIds);
      if (checked) set.add(String(id)); else set.delete(String(id));
      this.suspendedSelectedIds = Array.from(set);
    },
    toggleAllSuspended(checked) {
      this.suspendedSelectedIds = checked
        ? this.suspendedRows.map(r => String(r.invoice_id))
        : [];
    },

    /* ---------- pay ---------- */
    openPayModal() {
      const selected = this.payableRows.filter(r =>
        this.selectedInvoiceIds.includes(String(r.invoice_id))
      );
      if (selected.length === 0) return;

      const groups = {};
      for (const row of selected) {
        const sp = row.username || 'Unknown';
        if (!groups[sp]) groups[sp] = { salesperson: sp, rows: [], total: 0, reference: '', comments: '' };
        groups[sp].rows.push(row);
        groups[sp].total += this.payableCommission(row);
      }
      this.payGroups = Object.values(groups);
      this.payError = null;
      this.$bvModal.show('payroll-pay-modal');
    },
    async submitPayroll() {
      this.payError = null;
      if (!this.canSubmitPay) {
        this.payError = 'Reference # is required for every salesperson.';
        return;
      }
      this.isProcessing = true;
      try {
        const payload = {
          groups: this.payGroups.map(g => ({
            salesperson: g.salesperson,
            reference:   g.reference.trim(),
            comments:    (g.comments || '').trim(),
            amount:      g.total,
            invoices:    g.rows.map(r => ({
              invoice_id:         r.invoice_id,
              ts:                 r.ts,
              location_id:        r.location_id,
              payable_commission: this.payableCommission(r),
            })),
          })),
          processed_by: this.packet.userDetails.username || 'admin',
        };
        const res = await new Promise((resolve, reject) => {
          Requestor.postAsyncP(PAYROLL_ENDPOINTS.processPayroll, payload, this, resolve, reject);
        });
        if (res && res.data && res.data.error) {
          this.payError = res.data.error;
        } else {
          this.$bvToast.toast('Payroll processed successfully.', { variant: 'success', autoHideDelay: 4000 });
          this.$bvModal.hide('payroll-pay-modal');
          this.selectedInvoiceIds = [];
          await this.fetchReport();
          await this.fetchPaidReceipts();
          this.innerTab = 2;
        }
      } catch (e) {
        console.error(e);
        this.payError = 'Network error. Payroll not processed.';
      } finally {
        this.isProcessing = false;
      }
    },

    /* ---------- suspend / restore ---------- */
    openSuspendModal() {
      if (this.selectedInvoiceIds.length === 0) return;
      this.suspendReason = '';
      this.$bvModal.show('payroll-suspend-modal');
    },
    async submitSuspend() {
      if (!this.suspendReason || !this.suspendReason.trim()) return;
      this.isProcessing = true;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.postAsyncP(PAYROLL_ENDPOINTS.suspendCommission, {
            invoice_ids:  this.selectedInvoiceIds,
            reason:       this.suspendReason.trim(),
            suspended_by: this.packet.userDetails.username || 'admin',
          }, this, resolve, reject);
        });
        if (res && res.data && res.data.error) {
          this.$bvToast.toast(res.data.error, { variant: 'danger', autoHideDelay: 4000 });
        } else {
          this.$bvToast.toast(`Suspended ${this.selectedInvoiceIds.length} invoice(s).`, {
            variant: 'warning', autoHideDelay: 4000,
          });
          this.$bvModal.hide('payroll-suspend-modal');
          this.selectedInvoiceIds = [];
          await this.fetchReport();
          this.innerTab = 1;
        }
      } catch (e) {
        console.error(e);
        this.$bvToast.toast('Network error.', { variant: 'danger', autoHideDelay: 4000 });
      } finally {
        this.isProcessing = false;
      }
    },
    async restoreCommission() {
      if (this.suspendedSelectedIds.length === 0) return;
      this.isProcessing = true;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.postAsyncP(PAYROLL_ENDPOINTS.restoreCommission, {
            invoice_ids: this.suspendedSelectedIds,
          }, this, resolve, reject);
        });
        if (res && res.data && res.data.error) {
          this.$bvToast.toast(res.data.error, { variant: 'danger', autoHideDelay: 4000 });
        } else {
          this.$bvToast.toast(`Restored ${this.suspendedSelectedIds.length} invoice(s).`, {
            variant: 'success', autoHideDelay: 4000,
          });
          this.suspendedSelectedIds = [];
          await this.fetchReport();
          this.innerTab = 0;
        }
      } catch (e) {
        console.error(e);
        this.$bvToast.toast('Network error.', { variant: 'danger', autoHideDelay: 4000 });
      } finally {
        this.isProcessing = false;
      }
    },

    /* ---------- allow discount commission ---------- */
    async allowDiscountCommission() {
      const ids = this.selectedDiscountOverridable.map(r => r.invoice_id);
      if (ids.length === 0) return;
      this.isProcessing = true;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.postAsyncP(PAYROLL_ENDPOINTS.allowDiscountComm, {
            invoice_ids: ids,
            approved_by: this.packet.userDetails.username || 'admin',
          }, this, resolve, reject);
        });
        if (res && res.data && res.data.error) {
          this.$bvToast.toast(res.data.error, { variant: 'danger', autoHideDelay: 4000 });
        } else {
          for (const row of this.reportRows) {
            if (ids.includes(row.invoice_id)) row.commission_override = true;
          }
          this.$bvToast.toast(
            `Commission restored on ${ids.length} discounted invoice(s).`,
            { variant: 'success', autoHideDelay: 4000 }
          );
        }
      } catch (e) {
        console.error(e);
        this.$bvToast.toast('Network error.', { variant: 'danger', autoHideDelay: 4000 });
      } finally {
        this.isProcessing = false;
      }
    },

    /* ---------- receipt preview ---------- */
    openPayrollReceipt(rec) {
      this.currentReceipt = rec;
      this.$bvModal.show('payroll-receipt-modal');
    },
    printReceipt() { window.print(); },
  },
};
</script>

<style scoped>
/* =================== SHELL =================== */
.payroll-page {
  font-family: 'Manrope', system-ui, sans-serif;
  color: #221F1C;
  font-size: 15px;
}

/* =================== HERO =================== */
.pr-hero {
  background: #221F1C;
  color: #fff;
  border-radius: 16px;
  overflow: hidden;
  border-bottom: 4px solid #EE7A22;
  margin-bottom: 14px;
}
.pr-hero-grid { padding: 18px 24px; }
.pr-hero-title {
  font-family: 'Sora', sans-serif;
  font-size: 20px;
  font-weight: 700;
  display: flex; align-items: center; gap: 12px;
}
.pr-hero-sub { font-size: 13px; color: rgba(255,255,255,0.6); margin-top: 4px; }
.pr-count-pill {
  background: #EE7A22; color: #fff;
  font-size: 13px; font-weight: 800;
  padding: 3px 11px; border-radius: 999px;
}

/* =================== FILTERS =================== */
.pr-filterstrip {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 14px;
}
.pr-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #736C63;
  margin-bottom: 4px;
  display: block;
}
.pr-required { color: #C93C30; }

/* =================== KPIs =================== */
.pr-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 14px;
}
@media (max-width: 900px) { .pr-kpis { grid-template-columns: repeat(2, 1fr); } }
.pr-kpi {
  background: #fff;
  border: 1.5px solid #E5E1DA;
  border-radius: 14px;
  padding: 14px 16px;
}
.pr-kpi-label {
  font-size: 11px; font-weight: 800; letter-spacing: 0.08em;
  color: #736C63; text-transform: uppercase;
}
.pr-kpi-value {
  font-family: 'Sora', sans-serif;
  font-size: 22px; font-weight: 800; margin-top: 4px;
}
.pr-kpi-sub { font-size: 12px; color: #736C63; margin-top: 2px; }
.pr-kpi-unpaid    { border-color: #F1C40F; background: #FFFCF1; }
.pr-kpi-unpaid    .pr-kpi-value { color: #B7950B; }
.pr-kpi-selected  { border-color: #2ECC71; background: #F1FCF4; }
.pr-kpi-selected  .pr-kpi-value { color: #1E8449; }
.pr-kpi-suspended { border-color: #E74C3C; background: #FFF5F4; }
.pr-kpi-suspended .pr-kpi-value { color: #B03A2E; }
.pr-kpi-paid      { border-color: #3498DB; background: #F2F9FF; }
.pr-kpi-paid      .pr-kpi-value { color: #1F618D; }

/* =================== TOOLBAR =================== */
.pr-toolbar {
  display: flex; flex-wrap: wrap;
  align-items: center; justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: #FAF9F7;
  border-bottom: 1px solid #E5E1DA;
}
.pr-toolbar-info { font-size: 13px; }
.pr-toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.pr-warn-chip {
  display: inline-flex; align-items: center; gap: 4px;
  background: #FFF1E4; color: #D4620F;
  font-size: 12px; font-weight: 700;
  padding: 2px 8px; border-radius: 999px;
  margin-left: 8px;
  border: 1px solid #F5CFAE;
}
.pr-btn-primary {
  background: #EE7A22 !important;
  border-color: #EE7A22 !important;
  color: #fff !important;
  font-weight: 700;
}

/* =================== TABLE =================== */
.pr-table-wrap { overflow-x: auto; }
.pr-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.pr-table th {
  background: #221F1C; color: #fff;
  font-size: 12px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.05em;
  padding: 12px 10px;
  border-bottom: 3px solid #EE7A22;
  text-align: left;
  white-space: nowrap;
}
.pr-table th.text-right { text-align: right; }
.pr-table th.text-center { text-align: center; }
.pr-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #F1EEE9;
  font-size: 13.5px;
}
.pr-row:hover td { background: #FAF9F7; }
.pr-row.is-selected td { background: #FFF8F0; }
.pr-row.is-selected td:first-child { box-shadow: inset 3px 0 0 #EE7A22; }

.pr-payable { color: #D4620F; font-size: 15px; }
.pr-disc-flag { color: #B03A2E; font-weight: 700; }
.pr-mini { font-size: 10px; padding: 2px 6px; margin: 0 2px; }
.pr-link { color: #2563eb; font-weight: 700; text-decoration: underline; }
.pr-chip {
  display: inline-block;
  background: #E5E1DA; color: #221F1C;
  padding: 3px 8px; border-radius: 6px;
  font-size: 12px; font-weight: 700;
  margin-right: 4px; margin-bottom: 2px;
}

/* =================== EMPTY / LOADING =================== */
.pr-empty, .pr-loading {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 60px 20px;
  color: #736C63;
}
.pr-empty h3 {
  font-family: 'Sora', sans-serif;
  font-size: 20px;
  margin: 16px 0 8px;
  color: #221F1C;
}
.pr-empty-mini { padding: 30px; text-align: center; color: #736C63; }

/* =================== PAID STATS =================== */
.pr-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  padding: 16px;
  background: #FAF9F7;
  border-bottom: 1px solid #E5E1DA;
}
.pr-stat-card {
  background: #fff;
  border: 1.5px solid #E5E1DA;
  border-radius: 12px;
  padding: 12px 14px;
}
.pr-stat-card.is-active {
  border-color: #EE7A22;
  box-shadow: inset 3px 0 0 #EE7A22;
}
.pr-stat-name {
  font-family: 'Sora', sans-serif;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 8px;
  color: #221F1C;
}
.pr-stat-row { display: flex; justify-content: space-between; font-size: 13px; padding: 3px 0; }
.pr-stat-row span { color: #736C63; }
.pr-stat-row b { color: #221F1C; }

/* =================== MODAL / RECEIPT =================== */
.pr-modal-dialog { max-width: 1200px; width: 100%; }
.pr-suspend-dialog { max-width: 520px; width: 92%; }
.pr-modal-content {
  border: none;
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.35);
}
.pr-doc { position: relative; padding: 24px; font-family: 'Manrope', Arial, sans-serif; }
.pr-close {
  position: absolute; top: 12px; right: 16px;
  background: none; border: none; font-size: 26px;
  color: #999; cursor: pointer; z-index: 10;
}
.pr-close:hover { color: #000; }

.pr-doc-header {
  display: flex; justify-content: space-between;
  gap: 20px;
  border-bottom: 3px solid #EE7A22;
  padding-bottom: 14px; margin-bottom: 20px;
}
.pr-doc-label {
  font-size: 10px; letter-spacing: 0.12em;
  text-transform: uppercase; color: #736C63; font-weight: 700;
}
.pr-doc-title {
  font-family: 'Sora', sans-serif;
  font-size: 20px; font-weight: 800;
  color: #1E1B18; margin-top: 4px;
}
.pr-doc-sub { font-size: 12px; color: #736C63; margin-top: 2px; }
.pr-doc-meta { display: flex; flex-direction: column; gap: 8px; text-align: right; }
.pr-doc-meta span {
  font-size: 10px; text-transform: uppercase;
  letter-spacing: 0.08em; color: #736C63; font-weight: 700; display: block;
}
.pr-doc-meta b { font-size: 14px; color: #221F1C; }
.pr-underlined { text-decoration: underline; }

.pr-group {
  border: 1.5px solid #E5E1DA;
  border-radius: 10px;
  margin-bottom: 16px;
  overflow: hidden;
}
.pr-group-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 16px; background: #221F1C; color: #fff;
}
.pr-group-name { font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 700; }
.pr-group-count { font-size: 12px; color: rgba(255,255,255,0.6); margin-left: 8px; }
.pr-group-total { font-family: 'Sora', sans-serif; font-size: 18px; color: #FFB06B; }
.pr-group-fields {
  display: flex; gap: 12px;
  padding: 12px 16px;
  background: #FAF9F7;
  border-top: 1px solid #E5E1DA;
}

.pr-doc-table { width: 100%; border-collapse: collapse; }
.pr-doc-table th {
  background: #F1EEE9; color: #221F1C;
  font-size: 11px; font-weight: 800; text-transform: uppercase;
  text-align: left; padding: 8px 12px;
  border-bottom: 1.5px solid #E5E1DA;
}
.pr-doc-table th.text-right { text-align: right; }
.pr-doc-table td { padding: 8px 12px; border-bottom: 1px solid #F1EEE9; font-size: 13px; }
.pr-doc-table td.text-right { text-align: right; }

.pr-grand {
  display: flex; justify-content: space-between; align-items: center;
  background: #EE7A22; color: #fff;
  padding: 14px 18px; border-radius: 10px;
  font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 800;
  margin-top: 16px;
}
.pr-modal-actions {
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #E5E1DA;
}
.pr-comments-box {
  background: #FAF9F7; border: 1px solid #E5E1DA;
  border-radius: 8px; padding: 10px 12px;
  font-size: 13px; margin-top: 4px;
}
</style>

<style>
/* Inner tab styling (unscoped because BootstrapVue strips scoped attrs on nested tabs) */
.pr-tabs-inner > .nav-tabs {
  padding: 10px 16px 0;
  background: #FAF9F7;
  border-bottom: 1px solid #E5E1DA;
}
.pr-tabs-inner > .nav-tabs .nav-link {
  font-weight: 700;
  color: #736C63;
  border-radius: 8px 8px 0 0;
  border: 1.5px solid transparent;
  padding: 8px 18px;
  margin-bottom: -1px;
}
.pr-tabs-inner > .nav-tabs .nav-link:hover { color: #221F1C; }
.pr-tabs-inner > .nav-tabs .nav-link.active {
  background: #221F1C !important;
  color: #fff !important;
  border-color: #221F1C !important;
}
.pr-tabs-inner > .tab-content { background: #fff; padding: 0; }
</style>


// WEBPACK FOOTER //
// src/components/Accounting/PayrollPage.vue